import React from 'react';
import { useNavigate, NavLink } from 'react-router-dom';
import { useApp } from '@/context/AppContext';
import { Search, Bell, ChevronDown, LogOut, Check } from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import { ThemeToggle } from '@/components/ThemeToggle';

const globalModules = [
  { path: '/app/today', key: 'nav.today' },
  { path: '/app/finance', key: 'nav.finance' },
  { path: '/app/cash', key: 'nav.cash' },
  { path: '/app/plan', key: 'nav.plan' },
  { path: '/app/advisor', key: 'nav.advisor' },
];

export const TopBar = () => {
  const { t, tx, lang, toggleLang, data, company, companies, setCompanyId, logout } = useApp();
  const navigate = useNavigate();
  const unread = data?.notifications.filter((n) => n.unread).length || 0;

  const onLogout = () => { logout(); navigate('/'); };

  return (
    <header
      className="app-chrome fixed top-0 right-0 left-0 lg:left-[232px] z-30 h-16 flex items-center justify-between px-4 lg:px-6 border-b"
      data-testid="app-topbar"
    >
      <div className="flex items-center gap-1 flex-1 min-w-0">
        <div className="lg:hidden flex items-center mr-2">
          <span className="font-heading font-extrabold text-sm tracking-tight fp-page-title">FINPULSE</span>
        </div>
        <nav className="hidden md:flex items-center gap-1">
          {globalModules.map((m) => (
            <NavLink
              key={m.path}
              to={m.path}
              data-testid={`topnav-${m.key.split('.')[1]}`}
              className={({ isActive }) =>
                `px-3 h-9 flex items-center rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-zinc-900/10 text-zinc-900 dark:bg-white/[0.12] dark:text-white'
                    : 'text-zinc-500 hover:text-zinc-900 hover:bg-zinc-900/5 dark:text-zinc-400 dark:hover:text-white dark:hover:bg-white/[0.06]'
                }`
              }
            >
              {t(m.key)}
            </NavLink>
          ))}
        </nav>
      </div>

      <div className="flex items-center gap-1.5 sm:gap-2.5">
        <div className="hidden lg:flex items-center gap-2 w-56 h-9 px-3 rounded-lg bg-zinc-900/[0.04] dark:bg-white/[0.06] border border-transparent focus-within:border-cyan-400/40 transition-colors">
          <Search className="w-4 h-4 text-zinc-400" />
          <input data-testid="global-search" placeholder={t('common.search')} className="bg-transparent outline-none text-sm w-full text-zinc-800 dark:text-zinc-100 placeholder:text-zinc-500" />
        </div>

        <ThemeToggle className="bg-zinc-900/[0.04] hover:bg-zinc-900/[0.08] text-zinc-600 dark:bg-white/[0.06] dark:hover:bg-white/[0.12] dark:text-zinc-300" />

        <button onClick={toggleLang} data-testid="lang-toggle" className="flex items-center h-9 px-2.5 rounded-lg bg-zinc-900/[0.04] hover:bg-zinc-900/[0.08] dark:bg-white/[0.06] dark:hover:bg-white/[0.12] transition-colors text-xs font-semibold text-zinc-600 dark:text-zinc-300">
          <span className={lang === 'es' ? 'text-cyan-600 dark:text-cyan-400' : ''}>ES</span>
          <span className="mx-1 text-zinc-400 dark:text-zinc-600">/</span>
          <span className={lang === 'en' ? 'text-cyan-600 dark:text-cyan-400' : ''}>EN</span>
        </button>

        <button onClick={() => navigate('/app/notifications')} data-testid="notifications-bell" className="relative w-9 h-9 rounded-lg bg-zinc-900/[0.04] hover:bg-zinc-900/[0.08] dark:bg-white/[0.06] dark:hover:bg-white/[0.12] transition-colors flex items-center justify-center">
          <Bell className="w-[18px] h-[18px] text-zinc-600 dark:text-zinc-300" strokeWidth={2} />
          {unread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center">{unread}</span>
          )}
        </button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="user-menu" className="flex items-center gap-2 h-9 pl-1 pr-2 rounded-lg hover:bg-zinc-900/[0.06] dark:hover:bg-white/[0.08] transition-colors">
              <div className="w-7 h-7 rounded-full bg-gradient-to-br from-cyan-400 to-violet-500 flex items-center justify-center text-white text-xs font-bold">X</div>
              <span className="hidden sm:block text-sm font-medium text-zinc-700 dark:text-zinc-200">{data?.user}</span>
              <ChevronDown className="w-4 h-4 text-zinc-500" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-64 bg-white/90 backdrop-blur-xl border-white/40">
            <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-zinc-400">{tx({ es: 'Cambiar empresa', en: 'Switch company' })}</DropdownMenuLabel>
            {companies.map((c) => (
              <DropdownMenuItem key={c.id} data-testid={`switch-company-${c.id}`} onClick={() => setCompanyId(c.id)} className="gap-2 cursor-pointer">
                <div className="w-6 h-6 rounded-md flex items-center justify-center text-[10px] font-bold text-white" style={{ background: c.color }}>{c.logo}</div>
                <span className="flex-1 truncate text-sm">{c.name}</span>
                {c.id === company?.id && <Check className="w-4 h-4 text-cyan-600" />}
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onLogout} data-testid="logout-btn" className="gap-2 cursor-pointer text-rose-600 focus:text-rose-600">
              <LogOut className="w-4 h-4" /> {t('common.logout')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
};
